import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, User, WifiOff, Settings as SettingsIcon } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { SettingsModal } from './SettingsModal';

/**
 * Standard top bar used by Receptionist/Doctor/Billing/Owner views.
 * Shows: Back to roles | Clinic title (from settings) | Live · user · settings.
 */
export const RoleShell = ({ titlePrefix, userName = 'Demo User', isLive = true, children }) => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [showSettings, setShowSettings] = useState(false);

  const title = titlePrefix
    ? `${titlePrefix} · ${settings.clinic_name}`
    : settings.clinic_name;

  return (
    <div className="min-h-screen flex flex-col" data-testid="role-shell">
      <div className="fixed top-0 left-0 right-0 z-50 glass-primary" style={{ height: '68px' }}>
        <div className="h-full flex items-center justify-between px-7">
          <button
            onClick={() => navigate('/personas')}
            className="flex items-center gap-2 font-['Inter'] text-[14px] font-medium transition-colors duration-180 hover:text-[#1C1917]"
            style={{ color: '#44403C' }}
            data-testid="back-to-roles"
          >
            <ChevronLeft size={18} strokeWidth={1.75} />
            Back to roles
          </button>

          <h1 className="font-['Sora'] text-base font-semibold" style={{ color: '#1C1917' }} data-testid="role-shell-title">
            {title}
          </h1>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              {isLive ? (
                <>
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#16A34A' }} />
                  <span className="font-['Sora'] text-sm font-semibold" style={{ color: '#16A34A' }}>Live</span>
                </>
              ) : (
                <>
                  <WifiOff size={14} strokeWidth={2} style={{ color: '#B91C1C' }} />
                  <span className="font-['Sora'] text-sm font-semibold" style={{ color: '#B91C1C' }}>Offline</span>
                </>
              )}
            </div>
            <div className="glass-subtle flex items-center gap-2 px-3 py-1.5 rounded-full">
              <User size={15} strokeWidth={1.75} style={{ color: 'var(--color-slate)', opacity: 0.7 }} />
              <span className="font-['Inter'] text-sm font-medium" style={{ color: '#44403C' }}>{userName}</span>
            </div>
            <button
              onClick={() => setShowSettings(true)}
              className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center transition-all duration-180 hover:bg-[rgba(61,90,128,0.08)]"
              data-testid="settings-btn"
              aria-label="Clinic settings"
            >
              <SettingsIcon size={16} strokeWidth={1.75} style={{ color: '#44403C' }} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1" style={{ marginTop: '68px' }}>
        {children}
      </div>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </div>
  );
};

export default RoleShell;
